// dashboard/frontend/src/components/Campaign/ContactImport.js
import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    LinearProgress,
    Paper,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api/axios';

const ContactImport = ({ campaignId, onImportComplete }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [importResults, setImportResults] = useState(null);

    const downloadTemplate = () => {
        const template = 'first_name,last_name,email,company_name,title,phone,linkedin_url,website,contact_snapshot,business_context\n';
        const blob = new Blob([template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'contact_import_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setError(null);
        } else {
            setError('Please select a valid CSV file');
            setFile(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setError(null);
        
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post(
                `/campaigns/${campaignId}/import_contacts/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setImportResults(response.data);
            if (onImportComplete) {
                onImportComplete();
            }
        } catch (error) {
            setError('Failed to import contacts: ' + 
                    (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.type === 'text/csv') {
            setFile(droppedFile);
            setError(null);
        } else {
            setError('Please drop a valid CSV file');
            setFile(null);
        }
    };

    // Update the Box component to include drag handlers and dynamic styling
    return (
        <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
                Import Contacts
            </Typography>

            <Box my={2}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={downloadTemplate}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <DownloadIcon fontSize="small" />
                    Download CSV Template
                </Link>
            </Box>

            <Box
                sx={{
                    border: '2px dashed',
                    borderColor: isDragging ? 'primary.main' : '#ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    mt: 2,
                    cursor: 'pointer',
                    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                }}
                onClick={() => document.getElementById('file-input').click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    id="file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />
                <CloudUploadIcon sx={{ 
                    fontSize: 48, 
                    color: isDragging ? 'primary.main' : 'primary.light',
                    transition: 'color 0.3s ease'
                }} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {file ? file.name : 'Click to select or drag and drop a CSV file'}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {uploading && (
                <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                </Box>
            )}

            {importResults && (
                <Box sx={{ mt: 2 }}>
                    <Alert severity="success">
                        Successfully imported {importResults.message}
                    </Alert>
                    
                    {importResults.errors && importResults.errors.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Import Errors
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Row</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {importResults.errors.map((error, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{error}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
            )}

            <Box sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!file || uploading}
                >
                    {uploading ? 'Importing...' : 'Import Contacts'}
                </Button>
            </Box>
        </Paper>
    );
};

export default ContactImport;